import { isScheduledView, isUnscheduledView, View } from '../components/scheduling/ViewMenu';
import { resolveStoredScheduledView, resolveStoredUnscheduledView } from './getSelectedView';
import { isMapOnlyChromeView } from './schedulingPanels';
import { RouteExistenceTracker } from './routeExistenceTracker';

export interface ChooseTabInput {
  currentView: View | undefined;
  hasRoutes: boolean;
  isSynced: boolean;
  isWorkspaceRouteLoading: boolean;
  onSchedulingPage: boolean;
  // False for organizations whose Routes page has no unscheduled order tray, so
  // an empty date lands on Routes rather than a tray they can't switch back to.
  // See ui.canViewUnscheduledViews.
  allowUnscheduledViews?: boolean;
  // True only when the selected date's route-existence flipped (0 ↔ 1+) while
  // the user stayed on the same date — the one mid-session signal that should
  // re-decide the tab even though a view is already set.
  routeExistenceChanged?: boolean;
}

// The view this date will land on: keep the current sub-view if it's already
// on the correct Scheduled / Not-Scheduled side, otherwise restore the stored
// preference. Used by chooseTabForDate (whether to switch) and, when the view
// is unset, by fitDestinationViewForDate (which destination chrome to pad for).
// Organizations without the tray land on the Scheduled side whatever the date
// holds — an empty date has nowhere else to go for them.
export const destinationViewForDate = (
  hasRoutes: boolean,
  currentView?: View | null,
  allowUnscheduledViews: boolean = true,
): View => {
  if (hasRoutes || !allowUnscheduledViews) {
    if (currentView && isScheduledView(currentView)) {
      return currentView;
    }
    return resolveStoredScheduledView();
  }
  if (currentView && isUnscheduledView(currentView)) {
    return currentView;
  }
  return resolveStoredUnscheduledView();
};

// Which view's chrome to pad for when fitting the map. Tab picking uses
// destinationViewForDate (stored prefs when view is unset). Fit honors an
// explicit map-only view (collapsed panels); when view is unset it uses the
// same destination the tab picker will land on, so date-toggle fits pad for
// the gantt/list that is about to open.
export const fitDestinationViewForDate = (
  hasRoutes: boolean,
  currentView?: View | null,
  allowUnscheduledViews: boolean = true,
): View => {
  if (isMapOnlyChromeView(currentView)) {
    return currentView;
  }
  return destinationViewForDate(hasRoutes, currentView, allowUnscheduledViews);
};

// Decides which tab the user should be on given the selected date's state.
//
// The view query param is the source of truth: when one is present it reflects
// an explicit intent (a manual tab click, a deep link, or a toast such as
// "create orders from customers" that wants the Not Scheduled tab) and we
// honor it. We only auto-pick a tab when:
//   * no view is set yet (initial load, or a date toggle which clears the view
//     param at the call site so the new date gets a fresh decision), or
//   * the date's route-existence just changed mid-session (0 ↔ 1+), which is
//     the one data event that should override a view the user is sitting on.
//
// Returns `null` when the current view is already authoritative or when no
// decision can be made yet (loading, off-page, synced workspace). Kept out of
// the App.ts reaction so the matrix is testable in isolation.
export const chooseTabForDate = (input: ChooseTabInput): View | null => {
  if (input.isWorkspaceRouteLoading) return null;
  if (input.isSynced) return null;
  if (!input.onSchedulingPage) return null;

  const { hasRoutes, routeExistenceChanged, allowUnscheduledViews = true } = input;
  const currentView = input.currentView ?? null;

  // An explicit view is authoritative unless the date's route-existence just
  // flipped under the user.
  if (currentView && !routeExistenceChanged) return null;

  // Only flip across the Scheduled/Not-Scheduled boundary. If the current view
  // is already on the correct side, return null rather than re-issuing it — we
  // don't want to yank the user between sub-views. When we do land on a side,
  // restore the stored sub-view so an auto-pick never loses the user's
  // list/timeline (or list/table) preference.
  const destination = destinationViewForDate(hasRoutes, currentView, allowUnscheduledViews);
  return destination === currentView ? null : destination;
};

export interface TabReactionSignals {
  workspaceId: string | undefined;
  selectedDate: string | undefined;
  hasRoutes: boolean;
  isSynced: boolean;
  isWorkspaceRouteLoading: boolean;
  onSchedulingPage: boolean;
  currentView: View | undefined;
  allowUnscheduledViews?: boolean;
}

// Reaction-facing wrapper around chooseTabForDate. It applies the
// "is this a state we should act on" guard *before* advancing the
// route-existence tracker, so a still-loading (or off-page / synced) state
// never moves the settled baseline and is never read as a mid-session flip.
// Returns the view to switch to, or null to leave the current view alone.
export const resolveTabOnDataChange = (
  tracker: RouteExistenceTracker,
  signals: TabReactionSignals,
): View | null => {
  const {
    workspaceId, selectedDate, hasRoutes, isSynced, isWorkspaceRouteLoading, onSchedulingPage,
    currentView, allowUnscheduledViews,
  } = signals;

  if (isWorkspaceRouteLoading || isSynced || !onSchedulingPage) return null;

  // No decision without a workspace and a date. A workspace switch pushes a
  // bare pathname (selectWorkspace), so for a beat both `?date=` and `?view=`
  // are unset while sortedRoutes is empty. Deciding in that gap picks
  // Unscheduled off no data, and once dateValidator backfills the date the
  // tracker key changes — so the routes arriving reads as a navigation, not a
  // flip, and the premature pick sticks (ROUT-3258). dateValidator guarantees
  // a date lands on the scheduling page, so deferring never strands the page.
  if (!workspaceId || !selectedDate) return null;

  const routeExistenceChanged = tracker.observe({ workspaceId, selectedDate, hasRoutes });
  return chooseTabForDate({
    currentView,
    hasRoutes,
    isSynced,
    isWorkspaceRouteLoading,
    onSchedulingPage,
    allowUnscheduledViews,
    routeExistenceChanged,
  });
};
